import React, { useEffect, useState } from "react";
import { Edit3, Save, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { useI18n } from "@/lib/I18nContext";
import { pickText, toBilingual } from "@/lib/bilingual";
import BilingualEditor from "@/components/BilingualEditor";
import { toast } from "sonner";

const PAGE_FIELDS = {
  fase1: "fase1_subtitle",
  fase2: "fase2_subtitle",
};

/**
 * Renders an admin-editable bilingual subtitle paragraph.
 * Props:
 *  - pageKey: "fase1" | "fase2"
 *  - fallback: default text shown if no override stored
 */
export default function EditableSubtitle({ pageKey, fallback, className = "text-zinc-400 text-sm mt-3 max-w-2xl leading-relaxed" }) {
  const { user } = useAuth();
  const { lang } = useI18n();
  const field = PAGE_FIELDS[pageKey];
  const [stored, setStored] = useState(null); // {es,en} | string | null
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ es: "", en: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/page-content");
      setStored(r.data?.[field] ?? null);
    } catch {
      setStored(null);
    }
  };
  useEffect(() => { load(); }, [pageKey]);

  const displayed = pickText(stored, lang) || fallback;

  const startEdit = () => {
    setDraft(toBilingual(stored));
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = { es: (draft.es || "").trim(), en: (draft.en || "").trim() };
      const value = (cleaned.es || cleaned.en) ? cleaned : null;
      await api.put("/admin/page-content", { [field]: value });
      toast.success("Subtítulo guardado");
      setEditing(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="mt-3 max-w-3xl">
        <BilingualEditor
          value={draft}
          onChange={setDraft}
          multiline
          rows={3}
          placeholderEs={fallback}
          placeholderEn="(opcional) English version"
          testidPrefix={`subtitle-${pageKey}`}
        />
        <div className="flex gap-2 mt-2 flex-wrap items-center">
          <button onClick={save} disabled={saving} data-testid={`subtitle-${pageKey}-save`}
            className="bg-[var(--c-lime)] text-black font-bold uppercase tracking-wider px-3 py-1.5 text-[10px] disabled:opacity-50 flex items-center gap-1">
            <Save size={11} /> {saving ? "…" : "Guardar"}
          </button>
          <button onClick={() => setEditing(false)} className="border border-zinc-800 px-3 py-1.5 text-[10px] uppercase tracking-wider flex items-center gap-1">
            <X size={11} /> Cancelar
          </button>
          <div className="text-[10px] text-zinc-500 font-mono-data">Vacío en un idioma = usa el otro o el texto por defecto</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-2 max-w-2xl">
      <p className={className} data-testid={`subtitle-${pageKey}`}>{displayed}</p>
      {user?.is_admin && (
        <button onClick={startEdit} data-testid={`subtitle-${pageKey}-edit`}
          className="mt-3 p-1.5 border border-zinc-800 hover:border-[var(--c-lime)] hover:text-[var(--c-lime)]"
          title="Editar subtítulo">
          <Edit3 size={11} />
        </button>
      )}
    </div>
  );
}
